import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type TaskAction = 'claim' | 'release' | 'complete' | 'review' | 'undo' | 'help' | 'status';

const TaskTapRecoverable = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<'confirm' | 'processing' | 'success' | 'error'>('confirm');
  const [undoToken, setUndoToken] = useState('');
  const [timeLeft, setTimeLeft] = useState(30); // 30 second undo window

  const action = searchParams.get('action') as TaskAction;
  const taskId = searchParams.get('taskId');
  const taskTitle = searchParams.get('task');
  const reward = searchParams.get('reward');
  const workerId = searchParams.get('workerId');
  const workerName = searchParams.get('workerName');

  // Countdown timer for undo
  useEffect(() => {
    if (step === 'success' && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [step, timeLeft]);

  const handleConfirm = async () => {
    setStep('processing');

    try {
      const undoId = `undo_${Date.now()}`;
      setUndoToken(undoId);

      // Process the action
      switch (action) {
        case 'claim':
          // Store original state for undo
          await fetch('https://futr-bet-api.b-9f2.workers.dev/tasktap/store-undo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              undoToken: undoId,
              action: 'claim',
              taskId,
              originalState: { status: 'available' },
              expiresIn: 30 // 30 seconds to undo
            })
          });

          // Send claim notification with undo link
          const undoUrl = `https://futr.bet/tasktap/recoverable?action=undo&undoToken=${undoId}&originalAction=claim`;

          await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              text: `📋 Task claimed by ${workerName}\n*Task:* ${taskTitle}\n\n_Claimed by mistake? Undo within 30 seconds: ${undoUrl}_`,
              channel: 'C09H9EC5W7N'
            })
          });

          // Send DM with management options
          const dashboardUrl = `https://futr.bet/tasktap/dashboard/${taskId}`;
          await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              text: `✅ You claimed: ${taskTitle}\n\n📊 Dashboard: ${dashboardUrl}\n❌ Release: ${undoUrl}\n❓ Get Help: https://futr.bet/tasktap/help?taskId=${taskId}`,
              channel: workerId
            })
          });
          break;

        case 'complete':
          // Add confirmation requirement
          await fetch('https://futr-bet-api.b-9f2.workers.dev/tasktap/store-undo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              undoToken: undoId,
              action: 'complete',
              taskId,
              originalState: { status: 'in_progress' },
              expiresIn: 60 // 1 minute to undo completion
            })
          });

          await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              text: `✅ Task marked complete by ${workerName}\n*Task:* ${taskTitle}\n\n_Not ready? Undo: https://futr.bet/tasktap/recoverable?action=undo&undoToken=${undoId}_`,
              channel: 'C09H9EC5W7N'
            })
          });
          break;

        case 'undo':
          const token = searchParams.get('undoToken');
          const originalAction = searchParams.get('originalAction');

          // Fetch undo data
          const undoRes = await fetch(`https://futr-bet-api.b-9f2.workers.dev/tasktap/get-undo?token=${token}`);
          const undoData = await undoRes.json();

          if (undoData.valid) {
            // Restore original state
            await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                text: `↩️ Action undone: ${originalAction}\n*Task:* ${undoData.taskTitle}\n\nTask is now ${undoData.originalState.status}`,
                channel: 'C09H9EC5W7N'
              })
            });
          } else {
            throw new Error('Undo window expired');
          }
          break;

        case 'help':
          // Create support ticket
          const ticketId = `help_${Date.now()}`;
          await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              text: `🆘 Help requested for task: ${taskTitle}\n*Worker:* ${workerName}\n*Ticket:* ${ticketId}\n\nSupport will respond shortly.`,
              channel: 'C09H9EC5W7N'
            })
          });
          break;

        case 'status':
          // Show comprehensive task status
          const statusRes = await fetch(`https://futr-bet-api.b-9f2.workers.dev/tasktap/status?taskId=${taskId}`);
          const status = await statusRes.json();

          // This would show a dashboard with full history
          break;
      }

      setStep('success');

    } catch (error) {
      console.error('Error:', error);
      setStep('error');
    }
  };

  const handleUndo = async () => {
    if (undoToken && timeLeft > 0) {
      window.location.href = `https://futr.bet/tasktap/recoverable?action=undo&undoToken=${undoToken}&originalAction=${action}`;
    }
  };

  const getActionDetails = () => {
    switch (action) {
      case 'claim':
        return {
          title: 'Claim Task',
          description: `You're about to claim this task. You'll have exclusive access to work on it.`,
          warning: 'You can release it later if needed.',
          emoji: '📋'
        };
      case 'complete':
        return {
          title: 'Mark Complete',
          description: 'Submit this task for review. Make sure all requirements are met.',
          warning: 'The task will be reviewed before payment.',
          emoji: '✅'
        };
      case 'release':
        return {
          title: 'Release Task',
          description: 'Release this task back to the pool for others to claim.',
          warning: 'You will lose access to this task.',
          emoji: '🔄'
        };
      case 'undo':
        return {
          title: 'Undo Action',
          description: 'Revert the previous action.',
          warning: 'This will restore the previous state.',
          emoji: '↩️'
        };
      case 'help':
        return {
          title: 'Get Help',
          description: 'Request assistance with this task.',
          warning: 'A support ticket will be created.',
          emoji: '🆘'
        };
      default:
        return {
          title: action || 'Action',
          description: 'Process this action.',
          warning: '',
          emoji: '📝'
        };
    }
  };

  const details = getActionDetails();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {step === 'confirm' && (
          <div className="bg-gray-800 border border-gray-600 rounded-lg p-8">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">{details.emoji}</div>
              <h1 className="text-3xl font-bold mb-2">{details.title}</h1>
              <p className="text-gray-300">{details.description}</p>
              {details.warning && (
                <p className="text-yellow-400 text-sm mt-2">⚠️ {details.warning}</p>
              )}
            </div>

            {taskTitle && (
              <div className="bg-gray-900 rounded p-4 mb-6">
                <h2 className="text-lg font-semibold mb-2">Task Details</h2>
                <p className="text-gray-300">📝 {taskTitle}</p>
                {reward && <p className="text-green-400 mt-1">💰 {reward}</p>}
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={handleConfirm}
                className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-lg font-semibold transition-colors"
              >
                Confirm {details.title}
              </button>
              <button
                onClick={() => window.close()}
                className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold transition-colors"
              >
                Cancel
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center mt-4">
              You have 30 seconds to undo after confirming
            </p>
          </div>
        )}

        {step === 'processing' && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-400 mx-auto mb-4"></div>
            <p className="text-gray-400">Processing {action}...</p>
          </div>
        )}

        {step === 'success' && (
          <div className="bg-green-900/30 border border-green-500 rounded-lg p-8 text-center">
            <div className="text-6xl mb-4">✅</div>
            <h1 className="text-3xl font-bold mb-2">Success!</h1>
            <p className="text-xl mb-4">{details.title} completed</p>

            {timeLeft > 0 && undoToken && (
              <div className="mt-6 p-4 bg-gray-800 rounded">
                <p className="text-yellow-400 mb-2">Changed your mind?</p>
                <button
                  onClick={handleUndo}
                  className="px-6 py-2 bg-yellow-600 hover:bg-yellow-500 rounded font-semibold transition-colors"
                >
                  Undo ({timeLeft}s)
                </button>
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-green-800">
              <p className="text-xs text-gray-500">This window can be closed</p>
              <div className="flex gap-4 justify-center mt-4">
                <a
                  href={`https://futr.bet/tasktap/dashboard/${taskId}`}
                  className="text-blue-400 hover:underline text-sm"
                >
                  View Dashboard
                </a>
                <a
                  href={`https://futr.bet/tasktap/recoverable?action=help&taskId=${taskId}`}
                  className="text-blue-400 hover:underline text-sm"
                >
                  Get Help
                </a>
              </div>
            </div>
          </div>
        )}

        {step === 'error' && (
          <div className="bg-red-900/30 border border-red-500 rounded-lg p-8 text-center">
            <div className="text-6xl mb-4">❌</div>
            <h1 className="text-3xl font-bold mb-2">Error</h1>
            <p className="text-gray-300 mb-4">Something went wrong</p>

            <div className="space-y-2">
              <button
                onClick={handleConfirm}
                className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-lg font-semibold transition-colors"
              >
                Try Again
              </button>
              <a
                href={`https://futr.bet/tasktap/recoverable?action=help&taskId=${taskId}`}
                className="block w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold transition-colors"
              >
                Get Help
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskTapRecoverable;